// components/ShareModal.tsx — owner-side share modal for the in-app
// BookReader's "Share" button.
//
// Phase 1 stubbed this as a disabled placeholder. Phase 2 wires it up:
//   - On mount, POSTs /api/stories/:id/share to ensure the story has a
//     slug (idempotent).
//   - Shows the canonical URL with a copy button + a native-share
//     trigger when navigator.share is available.
//   - EPUB download link.
//   - Author toggles: allowContinuation, allowEpub, "list publicly"
//     (Phase 5 — disabled with a "coming soon" tooltip).
//   - Revoke button (returns to placeholder once removed).
//
// The modal styles itself with the same scenario theme tokens the host
// BookReader uses so it sits comfortably inside the reader's surface.

import {
  useCallback,
  useEffect,
  useState,
  type CSSProperties,
} from "react";
import { useTranslation } from "react-i18next";
import {
  shareStory,
  updateShareSettings,
  revokeShare,
  getShareArtifacts,
  BooksApiError,
  type ShareResponse,
  type ShareArtifactsResponse,
} from "../services/booksApi";

const STORY_WEB_ORIGIN =
  (import.meta.env.VITE_STORY_WEB_ORIGIN as string | undefined) ??
  // Same-origin fallback for local dev; in prod main.tsx never reaches this
  // branch because we're on www.* when the modal renders.
  (typeof window !== "undefined" ? window.location.origin : "");

export interface ShareModalProps {
  storyId: string;
  storyName: string;
  authorName: string;
  scenarioName: string;
  /** Scenario theme tokens (mirrors BookReader's local resolution). */
  accent: string;
  accentMuted: string;
  ink: string;
  inkMuted: string;
  paper: string;
  paperEdge: string;
  fontFamily: string;
  onClose: () => void;
}

/**
 * Build the absolute public reader URL for a slug. Honours
 * `VITE_STORY_WEB_ORIGIN` so dev builds can point at localhost while prod
 * resolves to story.emberhollow.ai.
 */
function buildShareUrl(slug: string): string {
  return `${STORY_WEB_ORIGIN}/s/${encodeURIComponent(slug)}`;
}

export default function ShareModal(props: ShareModalProps): JSX.Element {
  const { t } = useTranslation(["bookshelf", "common"]);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ShareResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
    "idle",
  );
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  // Phase 2b — AI-generated share copy. Fetched lazily once the story has a
  // public slug (i.e. published + not revoked). `artifactsState` tracks the
  // async lifecycle so we can render a brief "preparing share text…" line.
  const [artifacts, setArtifacts] = useState<ShareArtifactsResponse | null>(
    null,
  );
  const [artifactsState, setArtifactsState] = useState<
    "idle" | "loading" | "loaded" | "error"
  >("idle");
  const [artifactsError, setArtifactsError] = useState<string | null>(null);
  // Which artifact field was most recently copied (for per-field feedback).
  const [copiedField, setCopiedField] = useState<string | null>(null);
  /**
   * Server-side gate: the story has drifted to contentMark "mature" via
   * the adaptive intimacy mechanic, so public-library publishing is
   * blocked. The author keeps the private copy on their bookshelf with
   * full read/export — we render a quiet inline notice in place of the
   * share affordances. Detected by the 403 + code "above_share_threshold"
   * response from POST /api/stories/:id/share.
   */
  const [aboveShareThreshold, setAboveShareThreshold] = useState(false);

  // On mount: idempotent publish. If already shared, this just returns the
  // existing slug.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setAboveShareThreshold(false);
    (async () => {
      try {
        const res = await shareStory(props.storyId);
        if (cancelled) return;
        setData(res);
      } catch (err) {
        if (cancelled) return;
        if (
          err instanceof BooksApiError &&
          err.code === "above_share_threshold"
        ) {
          setAboveShareThreshold(true);
        } else {
          setError(
            err instanceof Error ? err.message : t("bookshelf:shareModal.failedShare"),
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [props.storyId]);

  // Esc to close.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") props.onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [props]);

  const shareUrl = data ? buildShareUrl(data.slug) : "";
  const isRevoked = data?.status === "revoked";

  const handleCopy = useCallback(async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopyState("copied");
      window.setTimeout(() => setCopyState("idle"), 1800);
    } catch {
      setCopyState("failed");
      window.setTimeout(() => setCopyState("idle"), 2400);
    }
  }, [shareUrl]);

  const handleNativeShare = useCallback(async () => {
    if (!navigator.share || !shareUrl || !data) return;
    try {
      await navigator.share({
        title: props.storyName,
        text: t("bookshelf:shareModal.shareIntent", {
          name: props.storyName,
          scenario: props.scenarioName,
        }),
        url: shareUrl,
      });
    } catch {
      // dismissed
    }
  }, [data, props.scenarioName, props.storyName, shareUrl, t]);

  const handleToggle = useCallback(
    async (patch: Parameters<typeof updateShareSettings>[1]) => {
      if (!data) return;
      setSaving(true);
      setError(null);
      try {
        const res = await updateShareSettings(props.storyId, patch);
        setData(res);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : t("bookshelf:shareModal.updateFailed"),
        );
      } finally {
        setSaving(false);
      }
    },
    [data, props.storyId, t],
  );

  const handleRevoke = useCallback(async () => {
    if (!data) return;
    setSaving(true);
    setError(null);
    try {
      await revokeShare(props.storyId);
      // Update local state to reflect revoked.
      setData({ ...data, status: "revoked" });
      setShowRevokeConfirm(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("bookshelf:shareModal.revokeFailed"),
      );
    } finally {
      setSaving(false);
    }
  }, [data, props.storyId, t]);

  const handleReshare = useCallback(async () => {
    await handleToggle({ status: "shared" });
  }, [handleToggle]);

  const handleEpubDownload = useCallback(() => {
    if (!data) return;
    const url = `/api/public/books/${encodeURIComponent(data.slug)}/epub`;
    window.open(url, "_blank");
  }, [data]);

  // ── Phase 2b: AI share copy ───────────────────────────────────────────
  // Fetch the share artifacts once the story has a live public slug. We key
  // the effect on a stable "is publicly shareable now" boolean so a toggle
  // change (allowEpub etc.) doesn't refetch, but a re-share does.
  const isShareable = Boolean(data && data.status !== "revoked");
  useEffect(() => {
    if (!isShareable) {
      setArtifacts(null);
      setArtifactsState("idle");
      setArtifactsError(null);
      return;
    }
    let cancelled = false;
    setArtifactsState("loading");
    setArtifactsError(null);
    (async () => {
      try {
        const res = await getShareArtifacts(props.storyId);
        if (cancelled) return;
        setArtifacts(res);
        setArtifactsState("loaded");
      } catch (err) {
        if (cancelled) return;
        setArtifactsError(
          err instanceof Error
            ? err.message
            : t("bookshelf:shareModal.artifactsFailed"),
        );
        setArtifactsState("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isShareable, props.storyId, t]);

  // Copy an arbitrary text snippet (one of the artifact fields). Reuses the
  // same clipboard path as the share-link copy, with per-field feedback.
  const handleCopyText = useCallback(async (field: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      window.setTimeout(() => setCopiedField(null), 1800);
    } catch {
      setCopiedField(`${field}:failed`);
      window.setTimeout(() => setCopiedField(null), 2400);
    }
  }, []);

  // Facebook share. The sharer dialog only reliably consumes the URL, so we
  // also copy the blurb to the clipboard as the suggested caption to paste.
  const handleFacebookShare = useCallback(async () => {
    if (!shareUrl) return;
    if (artifacts?.blurb) {
      try {
        await navigator.clipboard.writeText(artifacts.blurb);
        setCopiedField("blurb");
        window.setTimeout(() => setCopiedField(null), 1800);
      } catch {
        // Clipboard denied — proceed; the user can still type a caption.
      }
    }
    const fbUrl =
      "https://www.facebook.com/sharer/sharer.php?u=" +
      encodeURIComponent(shareUrl);
    window.open(fbUrl, "_blank", "noopener,noreferrer");
  }, [artifacts, shareUrl]);

  // ── Styles ──────────────────────────────────────────────────────────
  const backdrop: CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.65)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 95,
    padding: 16,
  };
  const card: CSSProperties = {
    width: "100%",
    maxWidth: 460,
    background: props.paper,
    border: `1px solid ${props.paperEdge}`,
    borderRadius: 4,
    padding: 24,
    color: props.ink,
    fontFamily: props.fontFamily,
    boxShadow: "0 12px 60px rgba(0,0,0,0.55)",
    maxHeight: "90vh",
    overflowY: "auto",
  };
  const headerRow: CSSProperties = {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    marginBottom: 4,
  };
  const heading: CSSProperties = {
    margin: 0,
    fontSize: "1.2rem",
    color: props.accent,
    fontWeight: 500,
  };
  const closeBtn: CSSProperties = {
    background: "transparent",
    color: props.inkMuted,
    border: "none",
    cursor: "pointer",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.85rem",
    padding: 4,
  };
  const subtitle: CSSProperties = {
    margin: "0 0 22px",
    color: props.inkMuted,
    fontStyle: "italic",
    fontSize: "0.86rem",
  };
  const urlBox: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 12px",
    background: "rgba(0,0,0,0.32)",
    border: `1px solid ${props.paperEdge}`,
    borderRadius: 2,
    marginBottom: 12,
  };
  const urlText: CSSProperties = {
    flex: 1,
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.78rem",
    color: props.ink,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };
  const smallAction: CSSProperties = {
    background: "transparent",
    color: props.accent,
    border: `1px solid ${props.accentMuted}`,
    padding: "6px 12px",
    fontSize: "0.78rem",
    cursor: "pointer",
    borderRadius: 2,
    fontFamily: "inherit",
  };
  const ghostAction: CSSProperties = {
    width: "100%",
    background: "transparent",
    color: props.inkMuted,
    border: `1px solid ${props.paperEdge}`,
    padding: "10px 14px",
    fontSize: "0.9rem",
    cursor: "pointer",
    borderRadius: 2,
    fontFamily: "inherit",
    marginBottom: 8,
    textAlign: "left",
  };
  const sectionTitle: CSSProperties = {
    margin: "20px 0 10px",
    fontSize: "0.72rem",
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    color: props.inkMuted,
    fontFamily: "'JetBrains Mono', monospace",
  };
  const toggleRow: CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 0",
    borderBottom: `1px solid ${props.paperEdge}`,
  };
  const toggleLabel: CSSProperties = {
    flex: 1,
    paddingRight: 12,
  };
  const toggleLabelText: CSSProperties = {
    fontSize: "0.92rem",
    color: props.ink,
  };
  const toggleLabelHint: CSSProperties = {
    fontSize: "0.78rem",
    color: props.inkMuted,
    fontStyle: "italic",
    marginTop: 2,
  };
  const errStyle: CSSProperties = {
    color: "#c47a5a",
    fontSize: "0.82rem",
    marginTop: 8,
  };
  const revokeBtn: CSSProperties = {
    background: "transparent",
    color: "#c47a5a",
    border: "1px solid rgba(196,122,90,0.4)",
    padding: "8px 14px",
    fontSize: "0.85rem",
    cursor: "pointer",
    borderRadius: 2,
    fontFamily: "inherit",
    marginTop: 16,
  };
  // ── Phase 2b artifact styles ─────────────────────────────────────────
  const artifactBlock: CSSProperties = {
    marginBottom: 14,
    padding: "10px 12px",
    background: "rgba(0,0,0,0.22)",
    border: `1px solid ${props.paperEdge}`,
    borderRadius: 2,
  };
  const artifactHeadRow: CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 6,
    gap: 8,
  };
  const artifactLabel: CSSProperties = {
    fontSize: "0.68rem",
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    color: props.inkMuted,
    fontFamily: "'JetBrains Mono', monospace",
  };
  const artifactBody: CSSProperties = {
    fontSize: "0.86rem",
    lineHeight: 1.55,
    color: props.ink,
    whiteSpace: "pre-wrap",
    margin: 0,
  };
  const tinyCopyBtn: CSSProperties = {
    background: "transparent",
    color: props.accent,
    border: `1px solid ${props.accentMuted}`,
    padding: "3px 9px",
    fontSize: "0.68rem",
    cursor: "pointer",
    borderRadius: 2,
    fontFamily: "'JetBrains Mono', monospace",
    flexShrink: 0,
  };
  const facebookBtn: CSSProperties = {
    width: "100%",
    background: "transparent",
    color: props.accent,
    border: `1px solid ${props.accentMuted}`,
    padding: "10px 14px",
    fontSize: "0.9rem",
    cursor: "pointer",
    borderRadius: 2,
    fontFamily: "inherit",
    marginBottom: 8,
    textAlign: "left",
  };

  if (loading) {
    return (
      <div
        style={backdrop}
        onClick={(e) => {
          if (e.target === e.currentTarget) props.onClose();
        }}
      >
        <div style={card}>
          <p style={{ color: props.inkMuted, textAlign: "center" }}>
            {t("bookshelf:shareModal.preparing")}
          </p>
        </div>
      </div>
    );
  }

  // Mature-register story: the server's share endpoint blocked
  // publishing via 403 + code "above_share_threshold". Render the quiet
  // inline notice in place of the share affordances. No moralising; no
  // call to "tone it down" — it's the player's story. The author
  // retains the private copy on their bookshelf with full read/export.
  if (aboveShareThreshold) {
    const disabledShareBtn: CSSProperties = {
      width: "100%",
      background: "transparent",
      color: props.inkMuted,
      border: `1px solid ${props.paperEdge}`,
      padding: "10px 14px",
      fontSize: "0.9rem",
      cursor: "not-allowed",
      borderRadius: 2,
      fontFamily: "inherit",
      opacity: 0.5,
    };
    const matureNotice: CSSProperties = {
      marginTop: 14,
      color: props.inkMuted,
      fontStyle: "italic",
      fontSize: "0.86rem",
      lineHeight: 1.5,
    };
    return (
      <div
        style={backdrop}
        onClick={(e) => {
          if (e.target === e.currentTarget) props.onClose();
        }}
      >
        <div style={card}>
          <div style={headerRow}>
            <h2 style={heading}>{t("bookshelf:shareModal.title")}</h2>
            <button
              type="button"
              style={closeBtn}
              onClick={props.onClose}
              aria-label={t("bookshelf:shareModal.closeAria")}
            >
              ✕
            </button>
          </div>
          <p style={subtitle}>
            {t("bookshelf:shareModal.byline", {
              name: props.storyName,
              author: props.authorName,
              scenario: props.scenarioName,
            })}
          </p>
          <button type="button" style={disabledShareBtn} disabled>
            {t("bookshelf:shareModal.shareDisabled")}
          </button>
          <p style={matureNotice}>
            {t("bookshelf:shareModal.matureNotice")}
          </p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div
        style={backdrop}
        onClick={(e) => {
          if (e.target === e.currentTarget) props.onClose();
        }}
      >
        <div style={card}>
          <div style={headerRow}>
            <h2 style={heading}>{t("bookshelf:shareModal.title")}</h2>
            <button type="button" style={closeBtn} onClick={props.onClose}>
              ✕
            </button>
          </div>
          <div style={errStyle}>{error ?? t("bookshelf:shareModal.failedShare")}</div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={backdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget) props.onClose();
      }}
    >
      <div style={card}>
        <div style={headerRow}>
          <h2 style={heading}>{t("bookshelf:shareModal.title")}</h2>
          <button
            type="button"
            style={closeBtn}
            onClick={props.onClose}
            aria-label={t("bookshelf:shareModal.closeAria")}
          >
            ✕
          </button>
        </div>
        <p style={subtitle}>
          {t("bookshelf:shareModal.byline", {
            name: props.storyName,
            author: props.authorName,
            scenario: props.scenarioName,
          })}
        </p>

        {isRevoked ? (
          <>
            <p style={{ color: props.inkMuted, fontSize: "0.9rem" }}>
              {t("bookshelf:shareModal.revokedBody")}
            </p>
            <button
              type="button"
              style={smallAction}
              onClick={handleReshare}
              disabled={saving}
            >
              {t("bookshelf:shareModal.reshare")}
            </button>
          </>
        ) : (
          <>
            <div style={urlBox}>
              <span style={urlText}>{shareUrl}</span>
              <button type="button" style={smallAction} onClick={handleCopy}>
                {copyState === "copied"
                  ? t("bookshelf:shareModal.copied")
                  : copyState === "failed"
                    ? t("bookshelf:shareModal.copyFailed")
                    : t("bookshelf:shareModal.copy")}
              </button>
            </div>

            {typeof navigator !== "undefined" && "share" in navigator && (
              <button type="button" style={ghostAction} onClick={handleNativeShare}>
                {t("bookshelf:shareModal.openShareSheet")}
              </button>
            )}

            {data.allowEpub && (
              <button type="button" style={ghostAction} onClick={handleEpubDownload}>
                {t("bookshelf:shareModal.downloadEpub")}
              </button>
            )}

            {/* Phase 2b — Facebook share + AI-generated share copy. */}
            <button type="button" style={facebookBtn} onClick={handleFacebookShare}>
              {t("bookshelf:shareModal.shareFacebook")}
            </button>

            <div style={sectionTitle}>{t("bookshelf:shareModal.shareText")}</div>

            {artifactsState === "loading" && (
              <p style={{ color: props.inkMuted, fontSize: "0.85rem", fontStyle: "italic" }}>
                {t("bookshelf:shareModal.preparingShareText")}
              </p>
            )}
            {artifactsState === "error" && (
              <p style={errStyle}>
                {artifactsError ?? t("bookshelf:shareModal.artifactsFailed")}
              </p>
            )}
            {artifactsState === "loaded" && artifacts && (
              <>
                {(
                  [
                    ["blurb", artifacts.blurb],
                    ["summary", artifacts.summary],
                    ["foreword", artifacts.foreword],
                    ["excerpt", artifacts.excerpt],
                  ] as const
                ).map(([field, text]) => (
                  <div key={field} style={artifactBlock}>
                    <div style={artifactHeadRow}>
                      <span style={artifactLabel}>
                        {t(`bookshelf:shareModal.artifact.${field}`)}
                      </span>
                      <button
                        type="button"
                        style={tinyCopyBtn}
                        onClick={() => handleCopyText(field, text)}
                      >
                        {copiedField === field
                          ? t("bookshelf:shareModal.copied")
                          : copiedField === `${field}:failed`
                            ? t("bookshelf:shareModal.copyFailed")
                            : t("bookshelf:shareModal.copy")}
                      </button>
                    </div>
                    <p style={artifactBody}>{text}</p>
                  </div>
                ))}
              </>
            )}

            <div style={sectionTitle}>{t("bookshelf:shareModal.settings")}</div>

            <div style={toggleRow}>
              <div style={toggleLabel}>
                <div style={toggleLabelText}>
                  {t("bookshelf:shareModal.allowContinuationLabel")}
                </div>
                <div style={toggleLabelHint}>
                  {t("bookshelf:shareModal.allowContinuationHint")}
                </div>
              </div>
              <input
                type="checkbox"
                checked={data.allowContinuation}
                disabled={saving}
                onChange={(e) =>
                  handleToggle({ allowContinuation: e.target.checked })
                }
              />
            </div>

            <div style={toggleRow}>
              <div style={toggleLabel}>
                <div style={toggleLabelText}>
                  {t("bookshelf:shareModal.allowEpubLabel")}
                </div>
                <div style={toggleLabelHint}>
                  {t("bookshelf:shareModal.allowEpubHint")}
                </div>
              </div>
              <input
                type="checkbox"
                checked={data.allowEpub}
                disabled={saving}
                onChange={(e) => handleToggle({ allowEpub: e.target.checked })}
              />
            </div>

            <div
              style={{ ...toggleRow, opacity: 0.6 }}
              title={t("bookshelf:shareModal.publicCatalogueTitle")}
            >
              <div style={toggleLabel}>
                <div style={toggleLabelText}>
                  {t("bookshelf:shareModal.publicCatalogueLabel")}
                </div>
                <div style={toggleLabelHint}>
                  {t("bookshelf:shareModal.publicCatalogueHint")}
                </div>
              </div>
              <input type="checkbox" disabled checked={false} />
            </div>

            {!showRevokeConfirm ? (
              <button
                type="button"
                style={revokeBtn}
                onClick={() => setShowRevokeConfirm(true)}
                disabled={saving}
              >
                {t("bookshelf:shareModal.revoke")}
              </button>
            ) : (
              <div style={{ marginTop: 16 }}>
                <p style={{ fontSize: "0.85rem", color: props.inkMuted }}>
                  {t("bookshelf:shareModal.revokeConfirm")}
                </p>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    type="button"
                    style={revokeBtn}
                    onClick={handleRevoke}
                    disabled={saving}
                  >
                    {t("bookshelf:shareModal.revokeYes")}
                  </button>
                  <button
                    type="button"
                    style={{ ...smallAction, color: props.inkMuted, borderColor: props.paperEdge }}
                    onClick={() => setShowRevokeConfirm(false)}
                    disabled={saving}
                  >
                    {t("common:buttons.cancel")}
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {error && <div style={errStyle}>{error}</div>}
      </div>
    </div>
  );
}
